import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Project } from '../../../models/Project';
import {
  RunningSite,
  ServiceDeployConfig,
  SiteBatch,
  SITE_BATCH_SERVICE_CATALOG,
} from '../../../models/SiteBatch';
import siteBatchService from '../../../services/SiteBatchService';
import deploymentService from '../../../services/DeploymentService';
import { ACTIVE_DEPLOYMENT_QUERY_KEY } from '../../../hooks/useActiveDeployment';
import { activeBatchesQueryKey } from '../../../hooks/useActiveBatches';
import { runningSitesQueryKey } from '../../../hooks/useRunningSites';
import { useAutoSwap, type AutoSwapResult } from '../../../hooks/useAutoSwap';
import type { DeployConfirmOptions } from '../dialogs/DeployConfirmDialog';

import { DeployTabType } from '../types';

export type SnackbarSeverity = 'success' | 'error';
export interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
}

export interface BatchModalState {
  open: boolean;
  projectId: string | null;
  batchId: number | null;
  siteName: string | null;
  initial: SiteBatch | null;
}

/**
 * State for the local-multisite-auto-swap confirmation dialog. Mounted
 * by SiteDashboardView and resolved by `acceptSwap`/`rejectSwap`. The
 * `resolve` callback is awaited inside confirmDeploy so the deploy
 * flow blocks until the user picks a side.
 */
export interface SwapDialogState {
  open: boolean;
  otherSite: string | null;
  targetSite: string | null;
  resolve: ((accepted: boolean) => void) | null;
}

const INITIAL_SNACKBAR: SnackbarState = { open: false, message: '', severity: 'success' };
const INITIAL_BATCH_MODAL: BatchModalState = {
  open: false,
  projectId: null,
  batchId: null,
  siteName: null,
  initial: null,
};
const INITIAL_SWAP_DIALOG: SwapDialogState = {
  open: false,
  otherSite: null,
  targetSite: null,
  resolve: null,
};

const BRANCH = 'develop';

interface UseDeployActionsOptions {
  /** Sites currently running per `useRunningSites`. Empty array on cold load. */
  runningSites: RunningSite[];
  /** site name → Project lookup so auto-swap can resolve projectId for stop. */
  sitesByName: Map<string, Project>;
}

/**
 * /deploy 페이지에서 사용하는 배포 액션 집합.
 *
 * - confirmDeploy: 배포 시작 (site-batch → 실패 시 legacy single-deploy fallback)
 * - stop: 현장 전체 서비스 중단
 * - openBatch: 진행 모달 열기
 * - snackbar / batchModal / deployTarget state 관리
 *
 * `type` 이 배포 대상을 정한다 — company: 사내 테스트 서버(company-deploy.yaml),
 * remote: 사내 이미지를 현장 통합서버로 승격(site-deploy.yaml, 대상 통합서버 id 필요).
 * 백엔드는 이 값을 batch + step 행에 기록하고 히스토리가 사내/현장을 이 값으로 나눈다.
 */
export default function useDeployActions(
  type: DeployTabType,
  options: UseDeployActionsOptions = { runningSites: [], sitesByName: new Map() },
) {
  const { runningSites, sitesByName } = options;
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<SnackbarState>(INITIAL_SNACKBAR);
  const [deployTarget, setDeployTarget] = useState<Project | null>(null);
  const [batchModal, setBatchModal] = useState<BatchModalState>(INITIAL_BATCH_MODAL);
  const [swapDialog, setSwapDialog] = useState<SwapDialogState>(INITIAL_SWAP_DIALOG);

  const { checkAndSwap, isSwapping } = useAutoSwap({ sitesByName });

  // Dialog accept/reject just unblocks the awaited Promise from confirmDeploy
  // and closes the dialog. The actual stop+wait+startBatch chain stays in
  // confirmDeploy so the auto-swap hook can remain pure (no side-effects on
  // batchModal/snackbar — those belong to this hook).
  const acceptSwap = useCallback(() => {
    swapDialog.resolve?.(true);
    setSwapDialog((s) => ({ ...s, open: false, resolve: null }));
  }, [swapDialog.resolve]);

  const rejectSwap = useCallback(() => {
    swapDialog.resolve?.(false);
    setSwapDialog((s) => ({ ...s, open: false, resolve: null }));
  }, [swapDialog.resolve]);

  const dismissSnackbar = useCallback(
    () => setSnackbar((s) => ({ ...s, open: false })),
    [],
  );

  const closeBatchModal = useCallback(
    () => setBatchModal((s) => ({ ...s, open: false })),
    [],
  );

  const invalidateBatchQueries = useCallback(
    async (siteId: string) => {
      // 중단/취소/재구동 직후 UI 가 stale 상태로 "배포 중" 을 계속 보이지
      // 않도록 관련 쿼리를 전부 즉시 refetch. legacy active_deployment 와
      // site-batch active-all, running-sites 모두 영향받으므로 셋 다 invalidate.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ACTIVE_DEPLOYMENT_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: activeBatchesQueryKey(siteId) }),
        queryClient.invalidateQueries({ queryKey: runningSitesQueryKey(siteId) }),
      ]);
    },
    [queryClient],
  );

  const openDeployConfirm = useCallback((site: Project) => {
    setDeployTarget(site);
  }, []);

  const closeDeployConfirm = useCallback(() => {
    setDeployTarget(null);
  }, []);

  const confirmDeploy = useCallback(
    async (site: Project, services?: ServiceDeployConfig[], options?: DeployConfirmOptions) => {
      const environment: 'company' | 'remote' = type === 'remote' ? 'remote' : 'company';
      const isRemote = environment === 'remote';

      // local-multisite-auto-swap: in local environments, if another site
      // is already running we can't just `compose up` over it (shared
      // container_names + ports). Ask the user to stop it first, await
      // the teardown, then continue with the normal startBatch flow.
      // No-op in production via isLocalEnvironment() inside useAutoSwap.
      // 현장 통합서버 배포는 사내 서버 구동과 무관하므로 auto-swap 을 하지 않는다.
      const swapResult: AutoSwapResult = isRemote
        ? { swapped: false, reason: 'not-local' }
        : await checkAndSwap({
            target: site,
            runningSites,
            confirmFn: (otherSite, targetSite) =>
              new Promise<boolean>((resolve) => {
                setSwapDialog({ open: true, otherSite, targetSite, resolve });
              }),
          });

      if (swapResult.swapped === false) {
        if (swapResult.reason === 'cancelled') {
          setSnackbar({
            open: true,
            message: '배포가 취소되었습니다.',
            severity: 'error',
          });
          return;
        }
        if (swapResult.reason === 'error') {
          setSnackbar({
            open: true,
            message: swapResult.message,
            severity: 'error',
          });
          return;
        }
        // 'not-local' / 'no-other-site' → 기존 흐름 그대로
      } else {
        setSnackbar({
          open: true,
          message: `${swapResult.stoppedSite} 중단 완료. ${site.name} 배포를 시작합니다.`,
          severity: 'success',
        });
      }

      // deploy-service-mode-select: services 가 빈 배열이면 backend 기본값
      // (8개 전부 build 모드)으로 동작하도록 undefined 로 내린다 (module-7
      // useDeployServiceSelection.toPayload() 계약과 일치). 한 서비스라도
      // reuse 면 module-7 이 8개 전체 config 를 반환하므로 그대로 전송.
      const allServices = SITE_BATCH_SERVICE_CATALOG.map((s) => s.appname);
      const payload = !services || services.length === 0 ? undefined : services;
      const serviceCount = payload?.length ?? allServices.length;

      const startFn = () =>
        siteBatchService.startBatch(site.id, {
          site: site.name,
          environment,
          services: payload,
          ...(isRemote ? { target_device_id: options?.targetDeviceId } : {}),
        });

      try {
        const batch = await startFn();
        setBatchModal({
          open: true,
          projectId: site.id,
          batchId: batch.batch_id,
          siteName: site.name,
          initial: batch,
        });
        await invalidateBatchQueries(site.id);
        setSnackbar({
          open: true,
          message: `${site.name} ${serviceCount}개 서비스 배포가 시작되었습니다`,
          severity: 'success',
        });
      } catch (e: any) {
        // 409 conflict = another batch is already running (or stuck) for this site.
        // Offer to cancel it and retry — this happens when the previous run's
        // webhook never arrived (missing BACKEND_WEBHOOK_URL in dev, network issue).
        if (e?.response?.status === 409) {
          const proceed = window.confirm(
            `${site.name}에 이미 진행 중인 배포가 있습니다.\n` +
              `진행 중인 배포를 취소하고 새로 시작할까요?`,
          );
          if (!proceed) {
            setSnackbar({
              open: true,
              message: '배포 취소됨. 상단 배지로 기존 배치를 확인할 수 있습니다.',
              severity: 'error',
            });
            return;
          }
          try {
            await siteBatchService.cancelActiveBatchBySite(site.id, site.name, 'operator retry');
            const batch = await startFn();
            setBatchModal({
              open: true,
              projectId: site.id,
              batchId: batch.batch_id,
              siteName: site.name,
              initial: batch,
            });
            await invalidateBatchQueries(site.id);
            setSnackbar({
              open: true,
              message: `${site.name} 이전 배치 취소 후 재배포 시작`,
              severity: 'success',
            });
            return;
          } catch (retryErr) {
            console.error('cancel+retry failed:', retryErr);
            setSnackbar({
              open: true,
              message: '재시도 실패. 잠시 후 다시 시도하거나 관리자에게 문의하세요.',
              severity: 'error',
            });
            return;
          }
        }
        // 원격은 legacy fallback(사내 배포)으로 빠지면 안 된다 — 다이얼로그가 거절 사유를 보여준다.
        if (isRemote) throw e;
        // Legacy single-service fallback — keeps the UI operable if the
        // batch feature is disabled on the backend (missing GITHUB_TOKEN etc.).
        console.warn('site-batch start failed, falling back to legacy trigger:', e);
        await deploymentService.triggerDeployment({ projectId: site.id, branch: BRANCH });
        await invalidateBatchQueries(site.id);
        setSnackbar({
          open: true,
          message: `${site.name} 배포가 시작되었습니다 (legacy)`,
          severity: 'success',
        });
      }
    },
    [type, invalidateBatchQueries, checkAndSwap, runningSites],
  );

  const openBatch = useCallback((site: Project, batchId: number) => {
    // Open modal immediately; useSiteBatchPolling will hydrate.
    setBatchModal({
      open: true,
      projectId: site.id,
      batchId,
      siteName: site.name,
      initial: null,
    });
  }, []);

  const stop = useCallback(
    async (site: Project) => {
      const ok = window.confirm(
        `${site.name} 현장의 모든 서비스를 중단할까요?\n` +
          `GitHub Actions 에서 "docker compose down" 이 실행되어 컨테이너가 내려갑니다.`,
      );
      if (!ok) return;
      try {
        await siteBatchService.stopSite(site.id, site.name);
        await invalidateBatchQueries(site.id);
        setSnackbar({
          open: true,
          message: `${site.name} 중단 요청 전송됨`,
          severity: 'success',
        });
      } catch (e) {
        console.error('stopSite failed:', e);
        setSnackbar({
          open: true,
          message: '중단 요청 실패. 잠시 후 다시 시도해주세요.',
          severity: 'error',
        });
      }
    },
    [invalidateBatchQueries],
  );

  return {
    // dialog/modal state
    deployTarget,
    batchModal,
    snackbar,
    swapDialog,
    isSwapping,
    // state setters / closers
    openDeployConfirm,
    closeDeployConfirm,
    closeBatchModal,
    dismissSnackbar,
    acceptSwap,
    rejectSwap,
    // actions
    confirmDeploy,
    openBatch,
    stop,
    // branch 상수 노출 (DeployConfirmDialog 등에서 재사용)
    branch: BRANCH,
  } as const;
}
